import { useQuery } from '@tanstack/react-query';
import { analyticsService } from '@/api/services/AnalyticsService';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import type { DateRange } from '@/lib/period';

// Раздел «Рынок» агента — server-state. Запрашиваем только для аутентифицированного агента (иначе бэкенд 403).

/** «Рынок сейчас» по культурам (текущее состояние; период не применяется). */
export function useAgentMarket() {
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  return useQuery({
    queryKey: ['agent', 'market'] as const,
    queryFn: ({ signal }) => analyticsService.getMarket(signal),
    enabled: isAuthenticated && isAgent,
    staleTime: 60_000,
  });
}

/** Drill-down рынка по культуре (классы качества + предоплата). Только при выбранной культуре. */
export function useAgentBreakdown(goodsType: string | null) {
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  return useQuery({
    queryKey: ['agent', 'breakdown', goodsType] as const,
    queryFn: ({ signal }) => analyticsService.getBreakdown(goodsType!, signal),
    enabled: isAuthenticated && isAgent && !!goodsType,
    staleTime: 60_000,
  });
}

/** Закрытые сделки за период по культуре (goodsType=null → все культуры). */
export function useAgentDeals(range: DateRange, goodsType: string | null) {
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  return useQuery({
    queryKey: ['agent', 'deals', range.from, range.to, goodsType] as const,
    queryFn: ({ signal }) =>
      analyticsService.getDeals({ from: range.from, to: range.to, goodsType }, signal),
    enabled: isAuthenticated && isAgent && !!range.from && !!range.to,
    staleTime: 60_000,
  });
}
